import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import CT from '@marketplace-ai/tags';
import { useEffect, useId, useState } from 'react';
import { Button } from '~/components';
import {
  formatCalendarDate,
  formatCents,
  formatProviderTier,
  formatRateBps,
} from '~/features/employer/billing/format';
import {
  ActivationPreview,
  FeeAuthorization,
  FeeSchedule,
  PlacementFeeSchedule,
  PlatformFeeSchedule,
} from '~/features/employer/billing/types';

type ActivationFeeDialogProps = {
  open: boolean;
  preview: ActivationPreview | null;
  onClose: () => void;
  /** Called with the fee authorization when the preview carries a fee schedule (any kind). */
  onConfirm: (feeAuthorization?: FeeAuthorization) => void;
  onManagePaymentMethod: () => void;
  /** [MCV-1858] Why the dialog was (re)opened, e.g. the fee schedule changed under the employer. */
  notice?: string | null;
  /**
   * `acceptListing` is the employer accepting an application on the listing's terms: the
   * provider still has to confirm, so nothing is charged yet.
   */
  variant?: 'accept' | 'acceptListing';
};

export const ActivationFeeDialog = ({
  open,
  preview,
  onClose,
  onConfirm,
  onManagePaymentMethod,
  notice = null,
  variant = 'accept',
}: ActivationFeeDialogProps) => {
  // [MCV-1858] The authorization belongs to one schedule: a new hash, a reopened dialog or a fresh
  // notice all start unchecked, so the employer never confirms a schedule they did not tick for.
  const [authorizedHash, setAuthorizedHash] = useState<string | null>(null);
  const feeSchedule = preview?.feeSchedule ?? null;
  const scheduleHash = feeSchedule?.scheduleHash ?? null;
  useEffect(() => {
    setAuthorizedHash(null);
  }, [open, scheduleHash, notice]);
  const authorizationId = useId();

  if (!preview) {
    if (!open) return null;
    return (
      <Dialog open={open} onClose={onClose} className="relative z-50">
        <DialogBackdrop className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm" />
        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <DialogPanel className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
              <p className="body-text text-grey-400">Loading commitment preview...</p>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    );
  }

  const missingPaymentMethod = preview.isCurrentUserPayer && !preview.paymentMethodOnFile;
  const acceptsListing = variant === 'acceptListing';
  const authorized = feeSchedule != null && authorizedHash === feeSchedule.scheduleHash;
  const needsAuthorization = feeSchedule != null && !missingPaymentMethod;
  const tierLabel = formatProviderTier(
    preview.providerTier ??
      (feeSchedule?.kind === 'PLACEMENT_FEE' ? feeSchedule.providerTier : null)
  );

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm" />
      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="relative w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary-t-900">
                <CreditCardIcon className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div className="flex-1">
                <DialogTitle className="text-lg font-semibold text-grey-100">
                  Match commitment
                </DialogTitle>
                <p className="mt-2 body-text text-grey-400">
                  {missingPaymentMethod
                    ? 'Add a payment method before accepting this offer.'
                    : acceptsListing
                      ? "Accept this application on the listing's terms. The provider must confirm before the contract is created and the commitment is charged."
                      : 'Confirm the commitment before accepting this offer.'}
                </p>
              </div>
            </div>

            {notice && (
              <p
                role="alert"
                data-cy={CT.marketResponses.feeScheduleChangedNotice}
                className="mt-4 rounded-lg border border-error bg-error-t-900 px-4 py-3 text-left body-text text-error"
              >
                {notice}
              </p>
            )}

            <div className="mt-5 rounded-lg border border-grey-900 divide-y divide-grey-900">
              <Row label="Engagement" value={preview.engagementType ?? 'Match activation'} />
              {tierLabel && <Row label="Provider type" value={tierLabel} />}
              <Row label="Commitment" value={formatCents(preview.grossAmountCents)} />
              {preview.creditAppliedCents > 0 && (
                <Row
                  label="Promotional credit"
                  value={`-${formatCents(preview.creditAppliedCents)}`}
                />
              )}
              <Row
                label={acceptsListing ? 'Due when the provider confirms' : 'Due today'}
                value={formatCents(preview.netAmountCents)}
                strong
              />
            </div>

            {feeSchedule && (
              <FeeScheduleSections
                feeSchedule={feeSchedule}
                legacyCredit={preview.creditAppliedCents > 0}
              />
            )}

            {needsAuthorization && feeSchedule && (
              <label
                htmlFor={authorizationId}
                className="mt-5 flex items-start gap-3 text-left text-sm"
              >
                <input
                  id={authorizationId}
                  type="checkbox"
                  data-cy={CT.marketResponses.feeAuthorizationCheckbox}
                  checked={authorized}
                  onChange={(event) =>
                    setAuthorizedHash(event.target.checked ? feeSchedule.scheduleHash : null)
                  }
                  className="mt-0.5 size-5 shrink-0 cursor-pointer rounded-md bg-grey-900 border border-grey-800 accent-primary-50 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary-50"
                />
                <span className="text-grey-100">
                  {feeSchedule.kind === 'PLACEMENT_FEE'
                    ? "I authorize Healthcare Labor Exchange to charge the commitment now and the placement fee balance above on the provider's start date to the payment method on file."
                    : 'I authorize Healthcare Labor Exchange to charge the commitment now and the platform fees above automatically on their billing dates to the payment method on file.'}
                </span>
              </label>
            )}

            <div className="mt-6 flex gap-3 justify-end">
              <Button type="button" colorVariant="secondary" onClick={onClose}>
                Cancel
              </Button>
              {missingPaymentMethod ? (
                <Button type="button" onClick={onManagePaymentMethod}>
                  <span className="inline-flex items-center gap-2">
                    <CreditCardIcon className="h-5 w-5" />
                    Add payment method
                  </span>
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={needsAuthorization && !authorized}
                  onClick={() =>
                    onConfirm(
                      feeSchedule
                        ? { scheduleHash: feeSchedule.scheduleHash, accepted: true }
                        : undefined
                    )
                  }
                >
                  {acceptsListing ? 'Accept application' : 'Accept offer'}
                </Button>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

const Row = ({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) => (
  <div className="flex items-center justify-between gap-4 px-4 py-3">
    <span className={strong ? 'font-semibold text-grey-100' : 'body-text text-grey-400'}>
      {label}
    </span>
    <span
      className={`text-right ${strong ? 'font-semibold text-grey-100' : 'body-text text-grey-100'}`}
    >
      {value}
    </span>
  </div>
);

const COMP_TYPE_SUFFIX: Record<string, string> = { HOUR: '/hr', SHIFT: '/shift', RVU: '/RVU' };

const formatCompensation = (compType: string | null, rate: string | null) => {
  if (rate == null || Number.isNaN(Number(rate))) return '—';
  const amount = formatCents(Math.round(Number(rate) * 100));
  return `${amount}${compType ? (COMP_TYPE_SUFFIX[compType] ?? '') : ''}`;
};

const formatBillingDates = ({
  billingDates,
  contractSummary,
  estimatedMonths,
}: PlatformFeeSchedule) => {
  if (billingDates.length === 0) return '—';
  const dates = billingDates.map(formatCalendarDate).join(' · ');
  if (contractSummary.openEnded) return `${dates} · then monthly`;
  if (estimatedMonths != null && estimatedMonths > billingDates.length)
    return `${dates} · then monthly until the end date`;
  return dates;
};

// [MCV-1933] Promotional credits are 50%-capped on fee invoices and never touch commitments.
const CREDIT_DISCLOSURE =
  "Promotional credits can cover up to 50% of each platform or placement fee invoice. They don't apply to commitments or listing fees.";

const ContractSummary = ({ feeSchedule }: { feeSchedule: FeeSchedule }) => {
  const { contractSummary: summary } = feeSchedule;
  return (
    <>
      <h3 className="mt-5 mb-2 text-base font-semibold text-grey-100">Contract summary</h3>
      <div className="rounded-lg border border-grey-900 divide-y divide-grey-900">
        <Row label="Start date" value={formatCalendarDate(summary.startDate)} />
        <Row
          label="End date"
          value={summary.openEnded ? 'Open-ended' : formatCalendarDate(summary.endDate)}
        />
        <Row label="Compensation" value={formatCompensation(summary.compType, summary.rate)} />
        {summary.numOfFtes != null && <Row label="FTEs" value={String(summary.numOfFtes)} />}
        <Row label="Annual contract value" value={formatCents(summary.annualContractValueCents)} />
      </div>
    </>
  );
};

const PlatformFeeSections = ({ feeSchedule }: { feeSchedule: PlatformFeeSchedule }) => (
  <>
    <h3 className="mt-5 mb-2 text-base font-semibold text-grey-100">Fee schedule</h3>
    <div className="rounded-lg border border-grey-900 divide-y divide-grey-900">
      <Row
        label="Platform fee"
        value={`${formatRateBps(feeSchedule.rateBps)} of monthly contract value (annual ÷ 12), billed monthly in arrears`}
      />
      <Row
        label="Estimated monthly platform fee"
        value={formatCents(feeSchedule.monthlyFeeCents)}
      />
      <Row label="Billing dates" value={formatBillingDates(feeSchedule)} />
      {feeSchedule.maxPeriods != null && (
        <Row
          label="Billing term"
          value={`Billed monthly for up to ${feeSchedule.maxPeriods} months`}
        />
      )}
      {feeSchedule.estimatedTotalCents != null && feeSchedule.estimatedMonths != null && (
        <Row
          label="Estimated total platform fees"
          value={`${formatCents(feeSchedule.estimatedTotalCents)} over ${feeSchedule.estimatedMonths} ${
            feeSchedule.estimatedMonths === 1 ? 'month' : 'months'
          }`}
        />
      )}
    </div>
    {feeSchedule.commitmentCents != null && feeSchedule.commitmentCents > 0 && (
      <p className="mt-3 body-text text-grey-400" data-cy={CT.marketResponses.feeCommitmentCredit}>
        {`Your commitment of ${formatCents(feeSchedule.commitmentCents)} is credited against these fees.`}
      </p>
    )}
  </>
);

const PlacementFeeSections = ({ feeSchedule }: { feeSchedule: PlacementFeeSchedule }) => (
  <>
    <h3 className="mt-5 mb-2 text-base font-semibold text-grey-100">Placement fee</h3>
    <div className="rounded-lg border border-grey-900 divide-y divide-grey-900">
      <Row label="Placement fee" value={formatCents(feeSchedule.placementFeeCents)} />
      <Row label="Commitment due now" value={formatCents(feeSchedule.commitmentCents)} />
      <Row label="Balance" value={formatCents(feeSchedule.balanceCents)} />
      <Row
        label="Balance due"
        value={`Due on the provider's start date, ${formatCalendarDate(feeSchedule.balanceDueDate)}`}
      />
    </div>
    <p className="mt-3 body-text text-grey-400">
      If the provider never starts, no balance is charged.
    </p>
  </>
);

// A commitment credit proves a LEGACY balance exists, but STANDARD credits may coexist.
const LEGACY_CREDIT_DISCLOSURE =
  'Legacy credit covers commitments, platform fees and placement fees up to your remaining legacy balance, but not listing fees or enterprise plans.';

const FeeScheduleSections = ({
  feeSchedule,
  legacyCredit,
}: {
  feeSchedule: FeeSchedule;
  legacyCredit: boolean;
}) => (
  <div className="text-left" data-cy={CT.marketResponses.feeScheduleSection}>
    <ContractSummary feeSchedule={feeSchedule} />
    {feeSchedule.kind === 'PLACEMENT_FEE' ? (
      <PlacementFeeSections feeSchedule={feeSchedule} />
    ) : (
      <PlatformFeeSections feeSchedule={feeSchedule} />
    )}
    <p className="mt-3 body-text text-grey-400" data-cy={CT.marketResponses.feeCreditDisclosure}>
      {legacyCredit && <>{LEGACY_CREDIT_DISCLOSURE} </>}
      {CREDIT_DISCLOSURE}
    </p>
  </div>
);
